import React from 'react'
import Datetime from 'react-datetime'
import SimpleReactValidator from 'simple-react-validator'
import { countWords } from '../../../utils/helpers'
import withRouterForwardRef from '../../../hooks/withRouterForwardRef'
import RequiredSymbol from '../../shared/RequiredSymbol'
import CompanyName from 'components/account/shared/Fields/CompanyName'
// import merge from 'lodash/merge'

interface CompanySubmitFormProps {
	onDataChange: Function
	data: Object
}

interface CompanySubmitFormState {
	name: string
	websiteUrl: string
	logoUrl: string
	foundedAt: string
	employeeCount: string
	description: string
	streetName: string
	iban: string
	houseNumber: string
	houseNumberExtension: string
	postalCode: string
	cityName: string
}

class CompanySubmitForm extends React.Component<
	CompanySubmitFormProps,
	CompanySubmitFormState
> {
	validator = new SimpleReactValidator({
		autoForceUpdate: this,
		validators: {
			maxwords: {
				// name the rule
				message: 'A maximum of :values words is allowed',
				rule: (val, params) => {
					return countWords(val) <= params
				},
				// @ts-ignore
				messageReplace: (message, params) => message.replace(':values', params), // optional
			},
			prefixedUrl: {
				message: 'Link must start with https:// e.g. https://www.google.com',
				rule: (val) => val.startsWith('https://'),
			},
		},
		locale: 'en',
		element: (message) => (
			<p className="text-red-500 text-xs mt-1">{message}</p>
		),
	})

	constructor(props: CompanySubmitFormProps) {
		super(props)
		this.state = {
			name: '',
			websiteUrl: '',
			logoUrl: '',
			foundedAt: '',
			employeeCount: '',
			description: '',
			streetName: '',
			houseNumber: '',
			houseNumberExtension: '',
			iban: '',
			postalCode: '',
			cityName: '',
		}

		this.handleChange = this.handleChange.bind(this)
		this.handleInputChange = this.handleInputChange.bind(this)
		this.handleDatePickerChange = this.handleDatePickerChange.bind(this)
		this.handleCompanyNameChange = this.handleCompanyNameChange.bind(this)
		this.changeParentData = this.changeParentData.bind(this)
	}

	handleChange(name, value) {
		this.setState(
			{
				[name]: value,
			} as unknown as Pick<
				CompanySubmitFormState,
				keyof CompanySubmitFormState
			>,
			() => this.changeParentData()
		)
	}

	handleInputChange(event: any) {
		const target = event.target
		const value = target.type == 'checkbox' ? target.checked : target.value
		const name = target.name

		this.handleChange(name, value)
	}

	handleDatePickerChange(moment, name) {
		let value = moment.toDate ? moment.toDate() : moment
		this.handleChange(name, value)
	}

	handleCompanyNameChange(companyData: any) {
		this.setState({ ...this.state, ...companyData }, () =>
			this.changeParentData()
		)
	}

	changeParentData() {
		this.props.onDataChange(
			'companyFormData',
			{ ...this.props.data, ...this.state },
			this.validator.allValid()
		)
	}

	componentDidMount() {
		this.setState({ ...this.state, ...this.props.data })
	}

	render() {
		return (
			<form onSubmit={this.changeParentData} className="pt-6 mb-4" noValidate>
				<div className="mb-4">
					<label htmlFor="name">
						<RequiredSymbol />
						Company name
					</label>
					<CompanyName
						value={this.state.name}
						validator={this.validator}
						changeData={this.handleCompanyNameChange}
					/>
					{this.validator.message('name', this.state.name, 'required|min:2')}
				</div>
				<div className="mb-4">
					<label htmlFor="website">
						<RequiredSymbol />
						Website
					</label>
					<input
						className="w-full"
						name="websiteUrl"
						value={this.state.websiteUrl}
						onChange={this.handleInputChange}
						onBlur={() => this.validator.showMessageFor('website')}
						id="websiteUrl"
						type="url"
						placeholder="https://..."
					/>
					{this.validator.message(
						'website',
						this.state.websiteUrl,
						'required|prefixedUrl|url'
					)}
				</div>
				<div className="mb-4">
					<label htmlFor="website">Logo url</label>
					<input
						className="w-full"
						name="logoUrl"
						value={this.state.logoUrl}
						onChange={this.handleInputChange}
						onBlur={() => this.validator.showMessageFor('logo')}
						id="logoUrl"
						type="url"
						placeholder="https://..."
					/>
					{this.validator.message(
						'logo',
						this.state.logoUrl,
						'prefixedUrl|url'
					)}
				</div>
				<div className="mb-4">
					<label>Founding date</label>
					<Datetime
						className={'inline-block'}
						closeOnSelect={true}
						closeOnClickOutside={true}
						inputProps={{
							placeholder: 'DD-MM-YYYY',
							readOnly: true,
							id: 'foundedAt',
							name: 'foundedAt',
							required: false,
						}}
						value={this.state.foundedAt}
						onChange={(moment) =>
							this.handleDatePickerChange(moment, 'foundedAt')
						}
						dateFormat="DD-MM-YYYY"
						timeFormat={false}
					/>
				</div>
				<div className="mb-4">
					<label htmlFor="employeeCount">Amount of employees</label>
					<input
						className="w-full"
						name="employeeCount"
						id="employeeCount"
						min="1"
						value={this.state.employeeCount}
						onChange={this.handleInputChange}
						onBlur={() => {
							this.validator.showMessageFor('employee count')
						}}
						type="number"
						placeholder="X"
					/>
					{this.validator.message(
						'employee count',
						this.state.employeeCount,
						'min:1,num|integer'
					)}
				</div>
				<div className="mb-6">
					<label htmlFor="description">
						<RequiredSymbol />
						Company description
					</label>
					<p className="text-sm mb-2">
						Tell us something about your company (max 150 words)
					</p>
					<textarea
						className="w-full"
						name="description"
						value={this.state.description}
						onChange={this.handleInputChange}
						onBlur={() => this.validator.showMessageFor('description')}
						id="description"
						placeholder="Description"
					></textarea>
					{this.validator.message(
						'description',
						this.state.description,
						'required|maxwords:150|min:3'
					)}
				</div>
				<div>
					<div className="flex flex-wrap -mx-2">
						<div className="flex-grow mb-6 px-2">
							<label htmlFor="streetName">
								<RequiredSymbol />
								Street
							</label>
							<input
								className="w-full"
								name="streetName"
								type="text"
								value={this.state.streetName}
								onChange={this.handleInputChange}
								onBlur={() => this.validator.showMessageFor('street name')}
								id="streetName"
								placeholder="Street name"
							/>
							{this.validator.message(
								'street name',
								this.state.streetName,
								'required'
							)}
						</div>
						<div
							className="flex-grow-0 mb-6 px-2"
							style={{ maxWidth: '130px' }}
						>
							<label htmlFor="houseNumber">
								<RequiredSymbol />
								House number
							</label>
							<input
								className="max-w-full"
								name="houseNumber"
								type="number"
								value={this.state.houseNumber}
								onChange={this.handleInputChange}
								onBlur={() => this.validator.showMessageFor('house number')}
								id="houseNumber"
								placeholder="House number"
							/>
							{this.validator.message(
								'house number',
								this.state.houseNumber,
								'required|min:0,num|integer'
							)}
						</div>
						<div className="flex-grow-0 mb-6 px-2">
							<label htmlFor="houseNumberExtension">Extension</label>
							<input
								name="houseNumberExtension"
								type="text"
								value={this.state.houseNumberExtension}
								onChange={this.handleInputChange}
								id="houseNumberExtension"
								placeholder="Extension"
							/>
						</div>
					</div>
				</div>
				<div className="mb-6">
					<div className="flex -mx-2">
						<div className="flex-grow-0 px-2" style={{ maxWidth: '120px' }}>
							<label htmlFor="postalCode">
								<RequiredSymbol />
								Postal code
							</label>
							<input
								className="w-full"
								name="postalCode"
								type="text"
								value={this.state.postalCode}
								onChange={this.handleInputChange}
								onBlur={() => this.validator.showMessageFor('postal code')}
								id="postalCode"
								placeholder="1234 XX"
							/>
							{this.validator.message(
								'postal code',
								this.state.postalCode,
								'required|min:4'
							)}
						</div>
						<div className="flex-grow px-2">
							<label htmlFor="cityName">
								<RequiredSymbol />
								City
							</label>
							<input
								className="w-full"
								name="cityName"
								type="text"
								value={this.state.cityName}
								onChange={this.handleInputChange}
								onBlur={() => this.validator.showMessageFor('city')}
								id="cityName"
								placeholder="City"
							/>
							{this.validator.message('city', this.state.cityName, 'required')}
						</div>
					</div>
				</div>
			</form>
		)
	}
}

export default withRouterForwardRef(CompanySubmitForm)
